// src/pages/MaintenanceLogPage.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Avatar,
  Chip,
  useTheme,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  FilterList as FilterListIcon,
  Build as BuildIcon,
  History as HistoryIcon,
  Engineering as TechIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  CleaningServices as CleaningIcon,
  BuildCircle as DiagnosticsIcon
} from '@mui/icons-material';

import { getData, saveData } from '../services/storageService';
import { useNotifications } from '../context/NotificationContext';

const MaintenanceLogPage = ({ user }) => {
  const theme = useTheme();
  const { showNotification } = useNotifications();
  
  const [events, setEvents] = useState([]);
  const [equipmentList, setEquipmentList] = useState([]);
  const [userList, setUserList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [newEvent, setNewEvent] = useState({ 
    equipmentId: '', 
    eventType: 'Ремонт', 
    description: '', 
    nextServiceDate: '',
    priority: 'medium'
  });
  const [filters, setFilters] = useState({ equipmentId: '', technician: '', eventType: '' });

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [eventsData, equipmentData, usersData] = await Promise.all([
          getData('maintenance_events'),
          getData('equipment'),
          getData('users')
        ]);
        setEvents((eventsData || []).sort((a, b) => new Date(b.date) - new Date(a.date)));
        setEquipmentList(equipmentData || []);
        setUserList((usersData || []).filter(u => u.role === 'technician'));
      } catch (error) {
        console.error("Error loading maintenance data:", error);
        showNotification('Error al cargar los registros de mantenimiento', 'error');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [showNotification]);

  const handleOpenDialog = () => {
    setNewEvent({ 
      equipmentId: '', 
      eventType: 'Ремонт', 
      description: '', 
      nextServiceDate: '',
      priority: 'medium'
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => setOpenDialog(false);

  const getEventTypeIcon = (eventType) => {
    switch (eventType) {
      case 'Mantenimiento Programado': return <CheckCircleIcon />;
      case 'Ремонт': return <BuildIcon />;
      case 'Limpieza': return <CleaningIcon />;
      case 'Diagnóstico': return <DiagnosticsIcon />;
      default: return <WarningIcon />;
    }
  };

  const getEventTypeColor = (eventType) => {
    switch (eventType) {
      case 'Mantenimiento Programado': return 'success';
      case 'Ремонт': return 'error';
      case 'Limpieza': return 'info';
      case 'Diagnóstico': return 'warning';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const handleSaveEvent = async () => {
    if (!newEvent.equipmentId || !newEvent.description) {
      showNotification('Por favor, complete todos los campos obligatorios', 'warning');
      return;
    }

    const finalEvent = {
      ...newEvent,
      id: Date.now(),
      date: new Date().toISOString(),
      technician: user.username,
      equipmentName: equipmentList.find(e => e.id === newEvent.equipmentId)?.name || 'N/A'
    };

    try {
      const updatedEvents = [finalEvent, ...events];
      await saveData('maintenance_events', updatedEvents);
      setEvents(updatedEvents);
      handleCloseDialog();
      showNotification('Registro de mantenimiento guardado correctamente', 'success');
    } catch (error) {
      console.error("Error saving maintenance event:", error);
      showNotification('Error al guardar el registro de mantenimiento', 'error');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewEvent(prev => ({ ...prev, [name]: value }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({...prev, [name]: value}));
  };

  const filteredEvents = useMemo(() => {
    return events.filter(event => {
      const equipmentMatch = filters.equipmentId ? event.equipmentId === filters.equipmentId : true;
      const technicianMatch = filters.technician ? event.technician === filters.technician : true;
      const eventTypeMatch = filters.eventType ? event.eventType === filters.eventType : true;
      return equipmentMatch && technicianMatch && eventTypeMatch;
    });
  }, [events, filters]);

  const stats = useMemo(() => {
    return {
      total: events.length,
      thisMonth: events.filter(e => {
        const eventDate = new Date(e.date);
        const now = new Date();
        return eventDate.getMonth() === now.getMonth() && eventDate.getFullYear() === now.getFullYear();
      }).length,
      pending: events.filter(e => e.nextServiceDate && new Date(e.nextServiceDate) > new Date()).length
    };
  }, [events]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Заголовок */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Avatar
          sx={{
            width: 80,
            height: 80,
            bgcolor: theme.palette.primary.main,
            mx: 'auto',
            mb: 2
          }}
        >
          <BuildIcon sx={{ fontSize: 40 }} />
        </Avatar>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          Registro de Mantenimiento
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Gestión y seguimiento de actividades de mantenimiento
        </Typography>
      </Box>

      {/* Статистика */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 3, textAlign: 'center' }}>
            <CardContent>
              <Typography variant="h3" color="primary" sx={{ fontWeight: 700 }}>
                {stats.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total de Registros
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 3, textAlign: 'center' }}>
            <CardContent>
              <Typography variant="h3" color="info.main" sx={{ fontWeight: 700 }}>
                {stats.thisMonth}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Este Mes
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 3, textAlign: 'center' }}>
            <CardContent>
              <Typography variant="h3" color="warning.main" sx={{ fontWeight: 700 }}>
                {stats.pending}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Próximos Servicios
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Панель керування */}
      <Card sx={{ mb: 4, borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon color="primary" />
              Registros de Mantenimiento ({filteredEvents.length})
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />} 
              onClick={handleOpenDialog}
              sx={{ borderRadius: 2 }}
            >
              Nuevo Registro
            </Button>
          </Box>

          {/* Фільтри */}
          <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <FilterListIcon color="action" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Equipo</InputLabel>
                  <Select 
                    name="equipmentId" 
                    value={filters.equipmentId} 
                    label="Equipo" 
                    onChange={handleFilterChange}
                  >
                    <MenuItem value=""><em>Todos los equipos</em></MenuItem>
                    {equipmentList.map(item => (
                      <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Técnico</InputLabel>
                  <Select 
                    name="technician" 
                    value={filters.technician} 
                    label="Técnico" 
                    onChange={handleFilterChange}
                  >
                    <MenuItem value=""><em>Todos los técnicos</em></MenuItem>
                    {userList.map(user => (
                      <MenuItem key={user.id} value={user.username}>{user.username}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo de Evento</InputLabel>
                  <Select 
                    name="eventType" 
                    value={filters.eventType} 
                    label="Tipo de Evento" 
                    onChange={handleFilterChange}
                  >
                    <MenuItem value=""><em>Todos los tipos</em></MenuItem>
                    <MenuItem value="Mantenimiento Programado">Mantenimiento Programado</MenuItem>
                    <MenuItem value="Ремонт">Reparación</MenuItem>
                    <MenuItem value="Limpieza">Limpieza</MenuItem>
                    <MenuItem value="Diagnóstico">Diagnóstico</MenuItem>
                    <MenuItem value="Otro">Otro</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        </CardContent>
      </Card>

      {/* Таблиця записів */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Fecha</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Equipo</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Tipo</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Descripción</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Técnico</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Próximo Servicio</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Prioridad</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEvents.map((event) => (
                  <TableRow 
                    key={event.id}
                    sx={{ 
                      '&:hover': { 
                        backgroundColor: theme.palette.action.hover 
                      } 
                    }}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {new Date(event.date).toLocaleDateString('es-ES')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(event.date).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {event.equipmentName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getEventTypeIcon(event.eventType)}
                        label={event.eventType}
                        color={getEventTypeColor(event.eventType)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {event.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.light }}>
                          <TechIcon sx={{ fontSize: 18 }} />
                        </Avatar>
                        <Typography variant="body2">
                          {event.technician}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {event.nextServiceDate ? (
                        <Chip
                          label={new Date(event.nextServiceDate).toLocaleDateString('es-ES')}
                          color={new Date(event.nextServiceDate) < new Date() ? 'error' : 'success'}
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No programado
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={event.priority === 'high' ? 'Alta' : event.priority === 'medium' ? 'Media' : 'Baja'}
                        color={getPriorityColor(event.priority)}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {filteredEvents.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <BuildIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No hay registros de mantenimiento
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Comience agregando el primer registro de mantenimiento
                      </Typography>
                      <Button 
                        variant="contained" 
                        startIcon={<AddIcon />} 
                        onClick={handleOpenDialog}
                        sx={{ borderRadius: 2 }}
                      >
                        Agregar Primer Registro
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Діалог додавання запису */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        fullWidth 
        maxWidth="md"
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Nuevo Registro de Mantenimiento
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Equipo *</InputLabel>
                <Select 
                  name="equipmentId" 
                  value={newEvent.equipmentId} 
                  label="Equipo *" 
                  onChange={handleInputChange}
                >
                  {equipmentList.map(item => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name} ({item.type})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Tipo de Evento</InputLabel>
                <Select 
                  name="eventType" 
                  value={newEvent.eventType} 
                  label="Tipo de Evento" 
                  onChange={handleInputChange}
                >
                  <MenuItem value="Mantenimiento Programado">Mantenimiento Programado</MenuItem>
                  <MenuItem value="Ремонт">Reparación</MenuItem>
                  <MenuItem value="Limpieza">Limpieza</MenuItem>
                  <MenuItem value="Diagnóstico">Diagnóstico</MenuItem>
                  <MenuItem value="Otro">Otro</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Prioridad</InputLabel>
                <Select 
                  name="priority" 
                  value={newEvent.priority} 
                  label="Prioridad" 
                  onChange={handleInputChange}
                >
                  <MenuItem value="low">Baja</MenuItem>
                  <MenuItem value="medium">Media</MenuItem>
                  <MenuItem value="high">Alta</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField 
                margin="normal" 
                name="nextServiceDate" 
                label="Próxima Fecha de Servicio" 
                type="date" 
                fullWidth 
                InputLabelProps={{ shrink: true }} 
                value={newEvent.nextServiceDate} 
                onChange={handleInputChange} 
              />
            </Grid>
            <Grid item xs={12}>
              <TextField 
                margin="normal" 
                name="description" 
                label="Descripción del Trabajo *" 
                fullWidth 
                multiline 
                rows={4} 
                value={newEvent.description} 
                onChange={handleInputChange} 
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} sx={{ borderRadius: 2 }}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSaveEvent} 
            variant="contained" 
            sx={{ borderRadius: 2 }}
          >
            Guardar Registro
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MaintenanceLogPage;


